import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../contexts'
import { getProjectIdFromPath } from '../utils/active-project'

/**
 * Reconciles in-memory auth to the active campaign on in-app navigation.
 *
 * `AuthProvider` is mounted above `HashRouter`, so its mount-only restore reads
 * the active campaign once and misses later campaign switches. This leaf lives
 * INSIDE the router (so `useLocation` is available) and re-runs the per-campaign
 * load/clear whenever the active campaign changes. It is intentionally a leaf —
 * independent of `ProjectProvider` — to avoid the `ProjectProvider`→`useAuth`
 * provider cycle (spec §7). On a non-campaign route (no active campaign) it
 * leaves auth untouched.
 */
export const AuthCampaignReconciler = () => {
  const location = useLocation()
  const { reconcileAuthForProject } = useAuth()

  const activeProjectId = useMemo(
    () => getProjectIdFromPath(location.pathname),
    [location.pathname]
  )

  useEffect(() => {
    if (!activeProjectId) return
    reconcileAuthForProject(activeProjectId)
  }, [activeProjectId, reconcileAuthForProject])

  return null
}
